import { useState } from "react";
import { SHIPPED_MODULES } from "@/data/plans";
import { calculatePricing } from "@/data/pricing-calculator";
import "./pricing-calculator.css";
const usd = (cents: number) =>
  `USD ${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export function PricingCalculator() {
  // Registration ships as one Phase 5 unit, including paid fulfilment acceptance.
  const registrationShipped = SHIPPED_MODULES.registration;
  const [events, setEvents] = useState("3"),
    [entries, setEntries] = useState("50"),
    [fee, setFee] = useState("0"),
    [annual, setAnnual] = useState(true),
    [country, setCountry] = useState("au"),
    [passed, setPassed] = useState(false);
  let result: ReturnType<typeof calculatePricing> | null = null;
  try {
    if (events.trim() && entries.trim() && (!registrationShipped || fee.trim()))
      result = calculatePricing({
        events: Number(events),
        entries: Number(entries),
        entryFee: registrationShipped ? Number(fee) : 0,
        annual,
        passFees: registrationShipped && country !== "au" && passed,
      });
  } catch {
    /* Inline validation retains the user's inputs. */
  }
  const rows = result
    ? [
        {
          key: "pass",
          name:
            result.passCents === 0
              ? "Free allowance / Event passes"
              : `Event pass ${result.size?.toUpperCase() ?? "custom quote"}`,
          base: result.passCents,
          fees: result.scorioFeeCents,
          athlete: result.athleteScorioCents,
          planned: false,
        },
        {
          key: "club",
          name: "Club Unlimited",
          base: result.clubCents,
          fees: result.scorioFeeCents,
          athlete: result.athleteScorioCents,
          planned: false,
        },
        {
          key: "tour",
          name: "Tour · planned",
          base: result.tourCents,
          fees: 0,
          athlete: Number(fee) * 100,
          planned: true,
        },
        {
          key: "liveheats",
          name: "LiveHeats · list credits",
          base: result.liveheatsCents,
          fees: result.liveheatsFeeCents,
          athlete: result.athleteLiveheatsCents,
          planned: false,
        },
        {
          key: "livejudge",
          name: "LiveJudge · contestants",
          base: result.livejudgeCents,
          fees: result.livejudgeFeeCents,
          athlete: result.athleteLivejudgeCents,
          planned: false,
        },
      ]
    : [];
  return (
    <section className="sc-container sc-section" aria-label="Pricing calculator">
      <p className="sc-eyebrow">Plan your calendar</p>
      <h2>Compare the cost of your events.</h2>
      <p>
        Estimates for a full year in USD, before tax and Stripe processing fees. Scorio paid offers
        are not on sale yet; Tour is planned.
      </p>
      <div className="sc-calculator-inputs">
        <label>
          Events per year
          <input
            type="number"
            min="0"
            max="10000"
            step="1"
            value={events}
            onChange={(e) => setEvents(e.target.value)}
          />
        </label>
        <label>
          Entries per event
          <input
            type="number"
            min="1"
            max="1000000"
            step="1"
            value={entries}
            onChange={(e) => setEntries(e.target.value)}
          />
        </label>
        {registrationShipped && (
          <>
            <label>
              Entry fee (USD, future scenario)
              <input
                type="number"
                min="0"
                max="1000000"
                step="0.01"
                value={fee}
                onChange={(e) => setFee(e.target.value)}
              />
            </label>
          </>
        )}
      </div>
      <div className="sc-interval" role="group" aria-label="Calculator billing interval">
        <button aria-pressed={!annual} onClick={() => setAnnual(false)}>
          Monthly
        </button>
        <button aria-pressed={annual} onClick={() => setAnnual(true)}>
          Annual
        </button>
      </div>
      <p className="sc-note">
        Monthly models 12 monthly payments; annual models one yearly payment. Event passes are
        one-off purchases.
      </p>
      {registrationShipped ? (
        <>
          <p className="sc-notice">
            Registration fee projection — not yet live. Scorio paid registration and the illustrated
            LiveJudge checkout fees are future scenarios, not available Scorio features.
          </p>
          <div className="sc-calculator-inputs">
            <label>
              Organiser location
              <select
                value={country}
                onChange={(e) => {
                  setCountry(e.target.value);
                  setPassed(false);
                }}
              >
                <option value="au">Australia</option>
                <option value="other">Outside Australia</option>
              </select>
            </label>
            <label className="sc-calculator-check">
              <input
                type="checkbox"
                checked={country !== "au" && passed}
                disabled={country === "au"}
                onChange={(e) => setPassed(e.target.checked)}
              />
              Fee passed to athletes (future scenario)
            </label>
          </div>
          {country === "au" && (
            <p className="sc-note">
              Passing on fees is unavailable for Australian organisers pending legal approval. Fees
              are absorbed in this estimate.
            </p>
          )}
        </>
      ) : (
        <p className="sc-notice">
          Paid registration is planned. Registration fees are not included in this calculator.
        </p>
      )}
      {!result ? (
        <p role="alert">
          Enter whole event and entry counts
          {registrationShipped ? ", and a non-negative entry fee" : ""}.
        </p>
      ) : (
        <>
          <p className="sc-note">
            {result.freeEvents} free small event{result.freeEvents === 1 ? "" : "s"} included. The
            first two events with up to 50 entries are free when no paid entries are taken. Paid
            events require a pass or subscription.
          </p>
          <div className="sc-calculator-results" aria-live="polite">
            {rows.map((row) => (
              <article
                key={row.key}
                className={`sc-panel ${result?.cheapest === row.key ? "sc-calculator-cheapest" : ""}`}
                aria-label={row.name}
              >
                <h3>{row.name}</h3>
                {result?.cheapest === row.key && (
                  <p className="sc-eyebrow">
                    Lowest modelled Scorio cost{row.planned ? " · planned offer" : ""}
                  </p>
                )}
                <p className="sc-calculator-total">
                  {row.base === null
                    ? "Contact for a quote"
                    : usd(row.base + (registrationShipped ? row.fees : 0))}
                  <small> / year</small>
                </p>
                <p className="sc-note">
                  {row.base === null
                    ? "Entry cap above published sizes"
                    : registrationShipped
                      ? `${usd(row.base)} base + ${usd(row.fees)} projected registration fees`
                      : "Event / subscription cost only"}
                </p>
                {registrationShipped && Number(fee) > 0 && (
                  <p className="sc-note">
                    Athlete entry price: {usd(Math.round(row.athlete))}
                    {country !== "au" && passed
                      ? " incl. platform fee"
                      : "; organiser absorbs platform fee"}
                  </p>
                )}
              </article>
            ))}
          </div>
        </>
      )}
      <p className="sc-note">
        Cost comparison only; features and availability differ. Entries are division entries, not
        unique people. LiveJudge comparison assumes one entry per contestant; teams are charged per
        member. LiveHeats list prices; bulk discounts unpublished. Stripe fees apply to all. No
        payment is taken by this calculator.
      </p>
      <p className="sc-note">
        Sources checked 27 October 2026:{" "}
        <a href="https://organiser.liveheats.com/pricing/" target="_blank" rel="noreferrer">
          LiveHeats list credits
        </a>
        ,{" "}
        <a
          href="https://help.liveheats.com/en/article/how-to-use-an-event-credit-108osep/"
          target="_blank"
          rel="noreferrer"
        >
          4% registration fee
        </a>
        ,{" "}
        <a href="https://livejudge.com/pricing/" target="_blank" rel="noreferrer">
          LiveJudge prices
        </a>
        . Vendor checkout availability may change; verify directly before buying.
      </p>
    </section>
  );
}
